import React, { useEffect, useState } from 'react';

// Mirrors the backend's GET /faucet-status status enum (healthy/low/critical).
// Any other value (including a malformed response) falls through to no badge
// at all — see renderless behavior below.
const STATUS_STYLES = {
  healthy: { dot: 'bg-green-500', text: 'text-green-300', border: 'border-green-500/30', bg: 'bg-green-500/10' },
  low: { dot: 'bg-yellow-500', text: 'text-yellow-300', border: 'border-yellow-500/30', bg: 'bg-yellow-500/10' },
  critical: { dot: 'bg-red-500', text: 'text-red-300', border: 'border-red-500/30', bg: 'bg-red-500/10' },
};

const STATUS_LABELS = {
  healthy: 'Faucet healthy',
  low: 'Faucet balance low — claims may be delayed',
  critical: 'Faucet temporarily empty — please check back later',
};

const DEFAULT_POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

// Purely informational — never gates the claim button. Renders nothing
// (rather than an error state) while loading or if the fetch fails, so a
// flaky/slow status endpoint can never break or block the claim page.
const FaucetStatusBadge = ({ apiUrl, pollIntervalMs = DEFAULT_POLL_INTERVAL_MS }) => {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchStatus = async () => {
      try {
        const response = await fetch(`${apiUrl}/faucet-status`);
        if (!response.ok) throw new Error(`faucet-status responded ${response.status}`);
        const data = await response.json();
        if (!cancelled) {
          setStatus(STATUS_LABELS[data.status] ? data.status : null);
        }
      } catch (error) {
        console.error('Failed to load faucet status:', error);
        if (!cancelled) setStatus(null);
      }
    };

    fetchStatus();
    const interval = setInterval(fetchStatus, pollIntervalMs);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [apiUrl, pollIntervalMs]);

  if (!status) return null;

  const style = STATUS_STYLES[status];

  return (
    <div
      role="status"
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium ${style.border} ${style.bg} ${style.text}`}
    >
      <span className={`h-2 w-2 rounded-full ${style.dot}`} />
      {STATUS_LABELS[status]}
    </div>
  );
};

export default FaucetStatusBadge;
